import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './FloatingFabs.css'
import { useGeneralSettings } from './useGeneralSettings'
import { appUrl } from '@astro/config'

const FALLBACK_WA_NUMBER = '919091840899'

/** Non-empty trimmed string, else '' — the API sends empty strings for unset fields. */
const val = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '')

/** wa.me wants digits; bare 10-digit Indian numbers get the 91 prefix. */
const toDigits = (raw) => {
  const digits = raw.replace(/\D/g, '')
  return digits.length === 10 ? `91${digits}` : digits
}
const WA_QUICK_REPLIES = [
  { icon: '💎', text: 'I need help selecting an authentic Rashi Ratna gemstone for my chart.', label: 'Gemstone Recommendation' },
  { icon: '📦', text: 'I would like to track my puja kit order shipment.', label: 'Track My Order' },
  { icon: '🔮', text: 'I would like to book a personal Vedic consultation.', label: 'Book a Consultation' },
]
const CONSULTATION_URL = appUrl('jyotish');
const SHOP_URL = appUrl('shop');
const ACADEMY_URL = appUrl('education');

const GENERIC_AI_REPLY = {
  paragraphs: [
    [{ type: 'text', text: 'Thank you. Your query has been recorded. For a full remedy prescription with human verification, please continue on WhatsApp.' }],
  ],
}

const AI_QUICK_TOPICS = [
  {
    icon: '🔮',
    label: 'Book consultation',
    userText: 'I would like to book a consultation.',
    reply: {
      paragraphs: [
        [
          { type: 'text', text: 'Blessings, seeker! To book a video consultation with Raju Maharaj Ji or one of our certified expert astrologers, please visit our ' },
          { type: 'link', text: 'Book/Join Consultation', href: CONSULTATION_URL },
          { type: 'text', text: ' app. There, you can schedule and purchase your personal video consultation.' },
        ],
      ],
    },
  },
  {
    icon: '📿',
    label: 'Gemstones & Poojas',
    userText: 'I would like to track my pooja or gemstone order.',
    reply: {
      paragraphs: [
        [
          { type: 'text', text: 'Blessings to you, Seeker! You can track your spiritual pooja or gemstone orders in the Divine Shop app. Once dispatched, tracking updates are available in your order panel there. ' },
          { type: 'link', text: 'Divine Shop', href: SHOP_URL },
        ],
      ],
    },
  },
  {
    icon: '🎓',
    label: 'Access courses',
    userText: 'How do I access my enrolled courses?',
    reply: {
      paragraphs: [
        [
          { type: 'text', text: 'Blessings, seeker! Your enrolled courses, lesson materials, and live interactive classes can all be accessed through the Astro Academy. Please visit: ' },
          { type: 'link', text: 'Astro Academy', href: ACADEMY_URL },
        ],
      ],
    },
  },
  {
    icon: '💳',
    label: 'Payment/Refunds',
    userText: 'I have a question about payment or refunds.',
    reply: {
      paragraphs: [
        [{ type: 'text', text: 'Blessings, seeker. Please do not worry. If your Razorpay payment was debited but the booking failed, the amount is automatically refunded to your original payment method within 5–7 business days.' }],
        [
          { type: 'text', text: 'You may try booking your consultation again through the ' },
          { type: 'link', text: 'Book/Join Consultation', href: CONSULTATION_URL },
          { type: 'text', text: ' app.' },
        ],
      ],
    },
  },
]

function CloseIcon({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" style={{ display: 'block' }}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function ExternalLinkIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  )
}

function AiMessageParagraphs({ paragraphs }) {
  return paragraphs.map((segments, pi) => (
    <p key={pi} style={{ margin: pi === 0 ? 0 : '8px 0 0' }}>
      {segments.map((seg, si) =>
        seg.type === 'link' ? (
          <a
            key={si}
            href={seg.href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#B45309', fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
          >
            {seg.text}
            <ExternalLinkIcon />
          </a>
        ) : (
          <span key={si}>{seg.text}</span>
        ),
      )}
    </p>
  ))
}

/**
 * The one floating action cluster (horoscope / WhatsApp / AI assistant)
 * rendered by every app. Keep it prop-free: any change here must apply to
 * the whole product family at once, exactly like @astro/dashboard.
 */
export default function FloatingFabs() {
  const navigate = useNavigate()
  const { settings } = useGeneralSettings()
  // WhatsApp destination from the API, with the previous number as fallback.
  const waNumber = toDigits(val(settings.whatsapp) || val(settings.phone)) || FALLBACK_WA_NUMBER
  const [expanded, setExpanded] = useState(false)
  const [activePanel, setActivePanel] = useState(null)
  const [aiMessages, setAiMessages] = useState([
    {
      id: 'seed',
      sender: 'ai',
      paragraphs: [
        [{ type: 'text', text: 'Namaste 🙏 Welcome to Astro Raju Maharaj Support. How may I assist you in your spiritual journey or help resolve an issue today?' }],
      ],
    },
  ])
  const [aiTyping, setAiTyping] = useState(false)
  const [aiInput, setAiInput] = useState('')
  const [waInput, setWaInput] = useState('')
  const [aiFocused, setAiFocused] = useState(false)
  const [waFocused, setWaFocused] = useState(false)

  const aiInputRef = useRef(null)
  const waInputRef = useRef(null)
  const switchTimerRef = useRef(null)

  const scrollToHoroscope = () => {
    // The Horoscope page lives on the website app (/horoscope). appUrl() is
    // environment-aware — localhost ports in dev, subdomains on staging/prod —
    // so compare its origin to ours instead of guessing from the hostname
    // (every app shares `localhost` in dev, differing only by port). When this
    // FAB is already on the website app, navigate client-side; from any other
    // app (shop/education/jyotish) do a full-page cross-app load. Correct on
    // both local and server.
    const target = appUrl('website', '/horoscope')
    if (new URL(target).origin === window.location.origin) navigate('/horoscope')
    else window.location.href = target
  }

  const closePanel = () => {
    window.clearTimeout(switchTimerRef.current)
    setActivePanel(null)
  }

  // Close the open panel on any click outside the panels / FAB cluster. The
  // FABs are excluded so opening/switching panels doesn't immediately re-close.
  useEffect(() => {
    if (!activePanel) return undefined
    const onPointerDown = (e) => {
      if (e.target.closest?.('#lf-chat-panel, #lf-wa-panel, #lf-root')) return
      window.clearTimeout(switchTimerRef.current)
      setActivePanel(null)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => document.removeEventListener('pointerdown', onPointerDown)
  }, [activePanel])

  // Surfaces without a prop path into this widget (e.g. the dashboard's
  // Help & Support "Live Chat" card) open the assistant panel by dispatching
  // this window event.
  useEffect(() => {
    const onOpenLiveChat = () => {
      setExpanded(true)
      if (activePanel !== 'chat') {
        window.clearTimeout(switchTimerRef.current)
        setActivePanel('chat')
        window.setTimeout(() => aiInputRef.current?.focus(), 60)
      }
    }
    window.addEventListener('astro:open-live-chat', onOpenLiveChat)
    return () => window.removeEventListener('astro:open-live-chat', onOpenLiveChat)
  }, [activePanel])

  const openPanel = (type) => {
    if (activePanel === type) {
      closePanel()
      return
    }
    window.clearTimeout(switchTimerRef.current)
    const delay = activePanel ? 160 : 0
    setActivePanel(null)
    switchTimerRef.current = window.setTimeout(() => {
      setActivePanel(type)
      window.setTimeout(() => {
        ; (type === 'chat' ? aiInputRef : waInputRef).current?.focus()
      }, 60)
    }, delay)
  }

  const toggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev
      if (!next) closePanel()
      return next
    })
  }

  const sendAiMessage = (text, reply = GENERIC_AI_REPLY) => {
    const msg = text.trim()
    if (!msg) return
    setAiMessages((prev) => [...prev, { id: `u-${Date.now()}`, sender: 'user', text: msg }])
    setAiTyping(true)
    window.setTimeout(() => {
      setAiTyping(false)
      setAiMessages((prev) => [
        ...prev,
        { id: `a-${Date.now()}`, sender: 'ai', paragraphs: reply.paragraphs },
      ])
    }, 1200)
  }

  const handleAiSubmit = (e) => {
    e.preventDefault()
    if (!aiInput.trim()) return
    sendAiMessage(aiInput)
    setAiInput('')
  }

  const handleQuickTopic = (topic) => sendAiMessage(topic.userText, topic.reply)

  const connectHumanAgent = () => openPanel('wa')

  const openWhatsappDirect = () => {
    window.open(`https://api.whatsapp.com/send?phone=${waNumber}`, '_blank')
  }

  const prefillWA = (text) => {
    setWaInput(text)
    waInputRef.current?.focus()
  }

  const handleWaSubmit = (e) => {
    e.preventDefault()
    const msg = waInput.trim()
    if (!msg) return
    window.open(`https://api.whatsapp.com/send?phone=${waNumber}&text=${encodeURIComponent(msg)}`, '_blank')
    setWaInput('')
    closePanel()
  }

  return (
    <>
      <div id="lf-root" className={expanded ? 'lf-expanded' : ''} style={{ position: 'fixed', bottom: '5.5rem', left: '1.5rem', zIndex: 999, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
        <button
          type="button"
          className="lf-fab lf-visible"
          onClick={scrollToHoroscope}
          style={{ background: 'linear-gradient(145deg,#7C3AED,#5B21B6)', boxShadow: '0 8px 22px rgba(124,58,237,0.42)' }}
          aria-label="Horoscope"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v4M12 18v4M2 12h4M18 12h4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M19.07 4.93l-2.83 2.83M7.76 16.24l-2.83 2.83" />
          </svg>
          <span className="lf-tooltip">Horoscope</span>
        </button>

        <button
          type="button"
          className="lf-fab lf-visible"
          onClick={() => openPanel('wa')}
          style={{ background: '#25d366', boxShadow: '0 8px 22px rgba(37,211,102,0.38)' }}
          aria-label="WhatsApp"
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.455 5.703 1.458h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
          <span className="lf-tooltip">WhatsApp</span>
        </button>

        <button
          type="button"
          className="lf-fab lf-visible"
          onClick={() => openPanel('chat')}
          style={{ background: 'linear-gradient(145deg,#E16A24,#C8560E)', boxShadow: '0 8px 22px rgba(225,106,36,0.42)' }}
          aria-label="AI Assistant"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
          </svg>
          <span className="lf-tooltip">AI Assistant</span>
        </button>

        <button
          type="button"
          id="lf-toggle"
          onClick={toggleExpanded}
          aria-label="Quick actions"
          aria-expanded={expanded}
          style={{ background: 'linear-gradient(145deg,#5D1020,#3A0814)', boxShadow: '0 8px 24px rgba(0,0,0,0.55),0 2px 6px rgba(0,0,0,0.3)', border: '1.5px solid rgba(245,158,11,0.28)' }}
        >
          <svg
            id="lf-ico-open"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="rgba(245,235,214,0.85)"
            aria-hidden="true"
            style={{ opacity: expanded ? 0 : 1, transform: expanded ? 'scale(0.5) rotate(180deg)' : 'scale(1) rotate(0deg)' }}
          >
            <circle cx="6" cy="6" r="2" />
            <circle cx="14" cy="6" r="2" />
            <circle cx="6" cy="14" r="2" />
            <circle cx="14" cy="14" r="2" />
          </svg>
          <svg
            id="lf-ico-close"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="rgba(245,235,214,0.85)"
            strokeWidth="2.5"
            strokeLinecap="round"
            aria-hidden="true"
            style={{ opacity: expanded ? 1 : 0, transform: expanded ? 'scale(1) rotate(0deg)' : 'scale(0.5) rotate(-180deg)' }}
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          <span className="lf-tooltip">Quick Actions</span>
        </button>
      </div>

      <div
        className={`lf-offcanvas-backdrop ${activePanel === 'chat' ? 'lf-backdrop-visible' : 'lf-backdrop-hidden'}`}
        onClick={closePanel}
        aria-hidden="true"
      />

      <div
        id="lf-chat-panel"
        className={`lf-panel lf-offcanvas ${activePanel === 'chat' ? 'lf-panel-visible' : 'lf-panel-hidden'}`}
        style={{ background: '#FDFAF5', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '16px 16px 14px', background: 'linear-gradient(135deg,#6B0F14,#3A0509)', borderBottom: '1px solid rgba(245,158,11,0.18)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
              <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'rgba(245,158,11,0.16)', border: '1.5px solid rgba(245,158,11,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#F5B04A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                </svg>
              </div>
              <div>
                <p style={{ fontSize: '13px', fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.25 }}>Astro Support</p>
                <p style={{ fontSize: '9px', fontWeight: 700, color: 'rgba(245,197,120,0.85)', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '2px 0 0' }}>
                  Instant AI Seeker Support
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={closePanel}
              style={{ width: '28px', height: '28px', borderRadius: '9px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.85)', cursor: 'pointer', padding: 0, transition: 'background 0.18s,color 0.18s', fontFamily: 'inherit' }}
              aria-label="Close chat"
            >
              <CloseIcon size={13} />
            </button>
          </div>
        </div>

        <div
          className="lf-scroll"
          style={{ flex: 1, padding: '16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', minHeight: 0, background: 'linear-gradient(165deg,#F7E8CE 0%,#FBF3E2 38%,#FDFAF5 75%)' }}
        >
          {aiMessages.map((m) =>
            m.sender === 'user' ? (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ background: 'linear-gradient(135deg,#8A1218,#5A0808)', color: '#fff', borderRadius: '14px', borderTopRightRadius: '4px', padding: '10px 13px', fontSize: '12px', lineHeight: 1.65, maxWidth: '88%' }}>
                  {m.text}
                </div>
              </div>
            ) : (
              <div key={m.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', maxWidth: '92%' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.40)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                  </svg>
                </div>
                <div style={{ background: '#fff', border: '1px solid rgba(245,158,11,0.18)', borderRadius: '14px', borderTopLeftRadius: '4px', padding: '11px 14px', fontSize: '12px', color: '#3B2A1A', lineHeight: 1.7, boxShadow: '0 2px 8px rgba(120,53,15,0.08)' }}>
                  <AiMessageParagraphs paragraphs={m.paragraphs} />
                </div>
              </div>
            ),
          )}
          {aiTyping && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', maxWidth: '92%' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.40)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                </svg>
              </div>
              <div style={{ background: '#fff', border: '1px solid rgba(245,158,11,0.18)', borderRadius: '14px', borderTopLeftRadius: '4px', padding: '10px 14px', fontSize: '12px', color: '#8A7460', fontStyle: 'italic' }}>
                Mapping charts…
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '14px 16px 16px', background: '#FDFAF5', borderTop: '1px solid rgba(245,158,11,0.22)', flexShrink: 0 }}>
          <p style={{ fontSize: '9.5px', fontWeight: 700, color: 'rgba(120,53,15,0.70)', letterSpacing: '0.1em', textTransform: 'uppercase', margin: '0 0 9px' }}>
            Common Topics:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            {AI_QUICK_TOPICS.map((t) => (
              <button
                key={t.label}
                type="button"
                onClick={() => handleQuickTopic(t)}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', fontWeight: 600, color: '#78350F', background: '#fff', border: '1px solid rgba(245,158,11,0.30)', borderRadius: '100px', padding: '8px 11px', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' }}
              >
                <span>{t.icon}</span>
                <span>{t.label}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={connectHumanAgent}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: 'linear-gradient(135deg,#8A1218,#5A0808)', color: '#fff', border: 'none', borderRadius: '11px', padding: '12px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '9px' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
            </svg>
            Connect With Human Agent
          </button>
          <button
            type="button"
            onClick={openWhatsappDirect}
            style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#fff', color: '#1D8A4E', border: '1.5px solid #25d366', borderRadius: '11px', padding: '11px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', marginBottom: '12px' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.455 5.703 1.458h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Prefer WhatsApp?
          </button>

          <form
            onSubmit={handleAiSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: `1.5px solid ${aiFocused ? 'rgba(245,158,11,0.60)' : 'rgba(245,158,11,0.22)'}`, borderRadius: '100px', padding: '4px 4px 4px 15px', transition: 'border-color 0.2s' }}
          >
            <input
              ref={aiInputRef}
              type="text"
              autoComplete="off"
              className="lf-bare-input"
              style={{ flex: 1, background: 'transparent', border: 'none', padding: '10px 0', fontSize: '12px', color: '#3B2A1A', fontFamily: 'inherit', minWidth: 0 }}
              placeholder="Type your message…"
              value={aiInput}
              onChange={(e) => setAiInput(e.target.value)}
              onFocus={() => setAiFocused(true)}
              onBlur={() => setAiFocused(false)}
            />
            <button
              type="submit"
              style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg,#F59E0B,#D97706)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}
              aria-label="Send"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </form>
        </div>
      </div>

      <div
        id="lf-wa-panel"
        className={`lf-panel ${activePanel === 'wa' ? 'lf-panel-visible' : 'lf-panel-hidden'}`}
        style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.1)', height: 'min(450px,calc(100vh - 3rem))', display: 'flex', flexDirection: 'column' }}
      >
        <div style={{ padding: '14px 16px 13px', background: '#075e54', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
            <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.455 5.703 1.458h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#fff', margin: 0, lineHeight: 1.2 }}>WhatsApp Live</p>
              <p style={{ fontSize: '9.5px', color: 'rgba(255,255,255,0.72)', margin: 0, lineHeight: 1.4 }}>Expert panel · replies in ~5 min</p>
            </div>
          </div>
          <button
            type="button"
            onClick={closePanel}
            style={{ width: '30px', height: '30px', borderRadius: '50%', background: 'rgba(255,255,255,0.12)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.75)', cursor: 'pointer', padding: 0, transition: 'background 0.18s', fontFamily: 'inherit' }}
            aria-label="Close WhatsApp"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div className="lf-wa-bg lf-scroll" style={{ flex: 1, padding: '14px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '9px', minHeight: 0 }}>
          <div style={{ maxWidth: '86%' }}>
            <div style={{ background: '#fff', borderRadius: '12px', borderTopLeftRadius: '4px', padding: '12px 14px', fontSize: '11px', lineHeight: 1.65, color: '#1C1917', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <p style={{ fontWeight: 700, color: '#075e54', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 5px' }}>Vedic Expert Panel</p>
              Connect with our human specialists for personalised consultations, gemstone guidance, or order support.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', alignItems: 'flex-start' }}>
            {WA_QUICK_REPLIES.map((qr) => (
              <button
                key={qr.label}
                type="button"
                onClick={() => prefillWA(qr.text)}
                style={{ fontSize: '10.5px', fontWeight: 500, color: '#374151', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '8px 13px', cursor: 'pointer', textAlign: 'left', boxShadow: '0 1px 2px rgba(0,0,0,0.06)', fontFamily: 'inherit' }}
              >
                {qr.icon} {qr.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding: '10px 12px', background: '#f0f0f0', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
          <form
            onSubmit={handleWaSubmit}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#fff', border: `1px solid ${waFocused ? 'rgba(245,158,11,0.7)' : '#e5e7eb'}`, borderRadius: '11px', padding: '4px 4px 4px 12px', transition: 'border-color 0.2s' }}
          >
            <input
              ref={waInputRef}
              type="text"
              autoComplete="off"
              className="lf-bare-input"
              style={{ flex: 1, background: 'transparent', border: 'none', padding: '9px 0', fontSize: '11px', color: '#1C1917', fontFamily: 'inherit', minWidth: 0 }}
              placeholder="Type to send on WhatsApp…"
              value={waInput}
              onChange={(e) => setWaInput(e.target.value)}
              onFocus={() => setWaFocused(true)}
              onBlur={() => setWaFocused(false)}
            />
            <button
              type="submit"
              style={{ width: '34px', height: '34px', borderRadius: '9px', background: '#25d366', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}
              aria-label="Send on WhatsApp"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </form>
        </div>
      </div>
    </>
  )
}
